import React, { useState, useEffect } from 'react';
import { Box, Typography, Paper, Button, Grid, Tabs, Tab } from '@mui/material';
import { oldFormatExample, newFormatExample } from '../utils/testAnalysisFormat';
import AnalysisResults from './module1/AnalysisResults';

// Real draft results format from API response
const realDraftResults = {
  "topics": [
    {
      "name": "Oil Change Service",
      "subtopics": [
        {
          "name": "General Oil Change",
          "stem_words": [
            "oil", "change", "near", "type", "kind", "top", "service"
          ],
          "keywords": [
            "oil change", "oil change near me", "what kind of oil change do i need",
            "what type of oil change do i need", "topping off oil"
          ]
        },
        {
          "name": "Oil Change Coupons & Pricing",
          "stem_words": [
            "coupon", "price", "synthetic", "full", "discount", "cost"
          ],
          "keywords": [
            "oil change coupons", "oil change coupon", "oil change coupons near me",
            "full synthetic oil change coupon", "full synthetic oil change coupons",
            "synthetic oil change coupons"
          ]
        },
        {
          "name": "Specific Vehicle Oil Change",
          "stem_words": [
            "bmw", "mercedes", "benz", "diesel", "vehicle", "specific"
          ],
          "keywords": [
            "bmw oil change near me", "mercedes oil change", "diesel oil change near me",
            "bmw oil change", "mercedes benz oil change"
          ]
        },
        {
          "name": "Oil Type Information",
          "stem_words": [
            "car", "take", "spec", "types"
          ],
          "keywords": [
            "what kind of oil does my car take", "what oil does my car take",
            "what type of oil does my car take", "types of oil for cars"
          ]
        }
      ]
    },
    {
      "name": "Oil System Problems & Diagnostics",
      "subtopics": [
        {
          "name": "Oil Leaks",
          "stem_words": [
            "leak", "repair", "park", "why"
          ],
          "keywords": [
            "car leaking oil", "car leaking oil when parked", "oil leaking from car",
            "oil leak repair near me", "why is my car leaking oil"
          ]
        },
        {
          "name": "Burning Oil",
          "stem_words": [
            "burn", "engine"
          ],
          "keywords": [
            "car burning oil", "why is my car burning oil", "engine burning oil", "burning oil"
          ]
        },
        {
          "name": "Oil Pressure Issues",
          "stem_words": [
            "low", "pressure", "symptom", "cause"
          ],
          "keywords": [
            "low oil pressure symptoms", "oil pressure warning light",
            "low oil pressure causes", "symptoms of low oil pressure"
          ]
        },
        {
          "name": "Oil Level & Warning Lights",
          "stem_words": [
            "light", "warn", "check", "gauge", "level", "smoke", "lose", "full"
          ],
          "keywords": [
            "oil light", "oil gauge", "oil pressure warning light", "engine oil light",
            "oil light on car", "car losing oil but no leak or smoke", "oil light in car",
            "oil warning light", "check oil light", "engine oil warning light",
            "oil light comes on but oil is full"
          ]
        }
      ]
    },
    {
      "name": "Brand Specific (Firestone)",
      "subtopics": [
        {
          "name": "Firestone Oil Change Service",
          "stem_words": [
            "firestone"
          ],
          "keywords": [
            "firestone oil change", "firestone oil change price"
          ]
        },
        {
          "name": "Firestone Oil Change Coupons",
          "stem_words": [],
          "keywords": [
            "oil change coupons", "firestone oil change coupon", "firestone oil change coupons",
            "firestone coupons oil change", "firestone coupon oil change"
          ]
        }
      ]
    },
    {
      "name": "Tire Services",
      "subtopics": [
        {
          "name": "Tire Purchase & Replacement",
          "stem_words": [
            "tire", "new", "buy", "replace"
          ],
          "keywords": [
            "new tires"
          ]
        },
        {
          "name": "Tire Problems & Repair",
          "stem_words": [
            "flat", "fix", "explosion", "burst"
          ],
          "keywords": [
            "tire explosion", "flat tire repair"
          ]
        }
      ]
    },
    {
      "name": "General Car Issues",
      "subtopics": [
        {
          "name": "Car Odors & Cleaning",
          "stem_words": [
            "smell", "odor", "clean", "bad", "need"
          ],
          "keywords": [
            "car smells bad", "car needs cleaning", "whats the smell in my car"
          ]
        }
      ]
    }
  ],
  "missing_themes": [
    "Brake Services", "Wheel Alignment", "Battery Services",
    "General Maintenance", "Air Conditioning", "Fluid Flushes"
  ],
  "workflow_id": "wf-20250423173410-bdbbf2e5",
  "client_id": "daveisawesomewithabighead",
  "timestamp": "2025-04-24T15:11:32.219639",
  "keyword_count": 55
};

// Create a nested version that simulates the actual API response structure
const nestedDraftResults = {
  results: {
    content: realDraftResults
  }
};

// Numeric format with themes 0, 1, 2 instead of names
const numericDraftResults = {
  "topics": [
    {
      "name": "0",
      "subtopics": [
        {
          "name": "Oil Change",
          "stem_words": ["oil", "change", "synthetic"],
          "keywords": ["oil change near me", "synthetic oil change"]
        },
        {
          "name": "Oil Pricing",
          "stem_words": ["price", "cost", "coupon"],
          "keywords": ["oil change cost", "oil change coupon"]
        }
      ]
    },
    {
      "name": "1",
      "subtopics": [
        {
          "name": "Oil Leaks",
          "stem_words": ["leak", "repair"],
          "keywords": ["car leaking oil", "oil leak repair"]
        }
      ]
    }
  ],
  "missing_themes": ["Brake Services", "Wheel Alignment"],
  "workflow_id": "wf-1234",
  "client_id": "client1",
  "timestamp": "2025-04-24T15:11:32.219639"
};

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`format-tabpanel-${index}`}
      aria-labelledby={`format-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const DebugAnalysisResults: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [displayData, setDisplayData] = useState<any>(null);

  useEffect(() => {
    console.log('Debug component mounted with test data:');
    console.log('Old format:', oldFormatExample);
    console.log('New format:', newFormatExample);
    console.log('Real draft results:', realDraftResults);
    console.log('Nested draft results:', nestedDraftResults);
    console.log('Numeric topic names:', numericDraftResults);
    
    // Default to real data
    setDisplayData(realDraftResults);
  }, []);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    
    // Set the display data based on the selected tab
    switch (newValue) {
      case 0:
        setDisplayData(realDraftResults);
        break;
      case 1:
        setDisplayData(oldFormatExample);
        break;
      case 2:
        setDisplayData(newFormatExample);
        break;
      case 3:
        setDisplayData(nestedDraftResults);
        break;
      case 4:
        setDisplayData(numericDraftResults);
        break;
      default:
        setDisplayData(realDraftResults);
    }
  };

  if (!displayData) {
    return <div>Loading test data...</div>;
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h5" gutterBottom>
          Analysis Results Debug View
        </Typography>
        <Typography variant="body1" paragraph>
          Use this tool to test different data formats for the AnalysisResults component.
          Select the format to display from the tabs below.
        </Typography>
        
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="Format tabs">
          <Tab label="Real Draft Results" />
          <Tab label="Old Format (Object)" />
          <Tab label="New Format (Array)" />
          <Tab label="Nested Format" />
          <Tab label="Numeric Topics" />
        </Tabs>
      </Paper>
      
      <Box sx={{ mb: 3 }}>
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Current Data Format Preview
          </Typography>
          <pre style={{ maxHeight: '200px', overflow: 'auto', fontSize: '12px', backgroundColor: '#f5f5f5', padding: '10px' }}>
            {JSON.stringify(displayData, null, 2)}
          </pre>
        </Paper>
      </Box>
      
      <TabPanel value={tabValue} index={0}>
        <AnalysisResults
          isProcessing={false}
          serviceAvailable={true}
          onReprocess={() => console.log('Reprocessing...')}
          onResultsLoaded={(results) => console.log('Results loaded:', results)} 
        data={realDraftResults} workflowId="wf-20250423173410-bdbbf2e5" />
      </TabPanel>
      
      <TabPanel value={tabValue} index={1}>
        <AnalysisResults
          isProcessing={false}
          serviceAvailable={true}
          onReprocess={() => console.log('Reprocessing...')}
          onResultsLoaded={(results) => console.log('Results loaded:', results)} 
        data={oldFormatExample} workflowId="test-workflow-123" />
      </TabPanel>
      
      <TabPanel value={tabValue} index={2}>
        <AnalysisResults 
          isProcessing={false}
          serviceAvailable={true}
          onReprocess={() => console.log('Reprocessing...')}
          onResultsLoaded={(results) => console.log('Results loaded:', results)}
        data={newFormatExample} workflowId="test-workflow-123" 
        
        />
      </TabPanel>
      
      <TabPanel value={tabValue} index={3}>
        <AnalysisResults
          isProcessing={false}
          serviceAvailable={true}
          onReprocess={() => console.log('Reprocessing...')}
          onResultsLoaded={(results) => console.log('Results loaded:', results)} 
        data={nestedDraftResults} workflowId="wf-20250423173410-bdbbf2e5" />
      </TabPanel>
      
      <TabPanel value={tabValue} index={4}>
        <AnalysisResults
          isProcessing={false}
          serviceAvailable={true}
          onReprocess={() => console.log('Reprocessing...')}
          onResultsLoaded={(results) => console.log('Results loaded:', results)} 
        data={numericDraftResults} workflowId="wf-1234" />
      </TabPanel>
    </Box>
  );
};

export default DebugAnalysisResults;
